import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';

import gameFormatService from '../../features/gameFormat/gameFormatService';
import leaderboardService from '../../features/leaderboard/leaderboardService';
import eventService from '../../features/event/eventService';
import axiosInstance from '../../features/axiosInstance';
import { getGameType, GameType } from '../../constants/gameConstants';
import { generateTeamName } from '../../helpers/teams';

const usePlayersImport = ({ events, selectedGameFormat, formMethods, event }) => {
  const { control, setValue } = formMethods;

  const watchImportPlayers = Boolean(useWatch({ control, name: 'importPlayers' }));
  const watchImportFromEventId = useWatch({ control, name: 'importFromEventId' });
  const watchImportLogType = useWatch({ control, name: 'importLogType' }) || 'current';
  const watchImportSkipSectionPositions =
    useWatch({ control, name: 'importSkipSectionPositions' }) ?? 0;
  const watchImportPositionFrom = useWatch({ control, name: 'importPositionFrom' }) || 1;
  const watchImportPositionTo = useWatch({ control, name: 'importPositionTo' }) || 1;
  const watchImportOrderByPbaRanking = Boolean(
    useWatch({ control, name: 'importOrderByPbaRanking' })
  );
  const watchImportPbaRankingDate = useWatch({ control, name: 'importPbaRankingDate' }) ?? null;
  const watchImportAutoGroupPlayers = Boolean(
    useWatch({ control, name: 'importAutoGroupPlayers' })
  );
  const watchImportShowPastEvents = Boolean(useWatch({ control, name: 'importShowPastEvents' }));

  const [currentLogSections, setCurrentLogSections] = useState([]);
  const [currentLogLoading, setCurrentLogLoading] = useState(false);
  const [pbaRankingsMap, setPbaRankingsMap] = useState(new Map());
  const [pbaRankingsLoading, setPbaRankingsLoading] = useState(false);
  const [pbaRankingsError, setPbaRankingsError] = useState(null);
  const prevImportEventIdRef = useRef(null);

  const importSourceEvent = useMemo(() => {
    if (!watchImportFromEventId) {
      return null;
    }

    return events.find((evt) => evt.id === watchImportFromEventId) || null;
  }, [events, watchImportFromEventId]);

  useEffect(() => {
    if (prevImportEventIdRef.current === watchImportFromEventId) {
      return;
    }

    prevImportEventIdRef.current = watchImportFromEventId;
    setValue('importPositionFrom', 1);
    setValue('importPositionTo', 1);
    setValue('importSkipSectionPositions', 0);
    setValue('importOrderByPbaRanking', false);
    setPbaRankingsMap(new Map());
    setPbaRankingsLoading(false);
    setPbaRankingsError(null);
  }, [watchImportFromEventId, setValue]);

  useEffect(() => {
    const loadCurrentLog = async () => {
      if (!watchImportPlayers || !importSourceEvent || watchImportLogType !== 'current') {
        setCurrentLogSections([]);
        return;
      }

      try {
        setCurrentLogLoading(true);
        const sections = importSourceEvent.sections || [];
        if (!sections.length) {
          setCurrentLogSections([]);
          return;
        }

        const logs = await Promise.all(
          sections.map((section) =>
            leaderboardService.getSectionLog(importSourceEvent.id, section.id)
          )
        );

        const nextLogs = sections.map((section, index) => ({
          sectionId: section.id,
          entries: Array.isArray(logs[index]) ? logs[index] : []
        }));
        setCurrentLogSections(nextLogs);
      } catch (error) {
        console.error('Failed to load current log:', error);
        setCurrentLogSections([]);
      } finally {
        setCurrentLogLoading(false);
      }
    };

    loadCurrentLog();
  }, [watchImportPlayers, importSourceEvent, watchImportLogType]);

  useEffect(() => {
    if (!watchImportOrderByPbaRanking) {
      setPbaRankingsMap(new Map());
      setPbaRankingsLoading(false);
      setPbaRankingsError(null);
      return;
    }

    let isActive = true;
    const controller = new AbortController();

    const loadRankings = async () => {
      try {
        setPbaRankingsLoading(true);
        setPbaRankingsError(null);
        const dateParam = watchImportPbaRankingDate
          ? typeof watchImportPbaRankingDate.format === 'function'
            ? watchImportPbaRankingDate.format('YYYY-MM-DD')
            : String(watchImportPbaRankingDate)
          : undefined;
        const response = await axiosInstance.get('/event/pba-rankings', {
          params: dateParam ? { date: dateParam } : {},
          signal: controller.signal
        });
        const rankings = response?.data?.data?.rankings || [];
        const nextMap = new Map();

        rankings.forEach((entry) => {
          if (entry?.bsa_number != null) {
            nextMap.set(String(entry.bsa_number).trim(), Number(entry.rank));
          }
        });

        if (isActive) {
          setPbaRankingsMap(nextMap);
        }
      } catch (error) {
        if (error?.name === 'AbortError') {
          return;
        }

        console.error('Failed to load PBA rankings:', error);
        if (isActive) {
          setPbaRankingsMap(new Map());
          setPbaRankingsError('Failed to load PBA rankings.');
        }
      } finally {
        if (isActive) {
          setPbaRankingsLoading(false);
        }
      }
    };

    loadRankings();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [watchImportOrderByPbaRanking, watchImportPbaRankingDate]);

  const getTeamSize = (gameType) => {
    if (!gameType) {
      return null;
    }

    const limit = gameFormatService.getGameTypeLimit(gameType);
    return limit ? limit / 2 : null;
  };

  const getBaseLogTeams = (sourceEvent, skipSectionPositions) => {
    if (!sourceEvent) {
      return [];
    }

    const sections = sourceEvent.sections || [];
    const eventTeams = sourceEvent.teams || [];
    if (!sections.length) {
      return eventTeams;
    }

    const orderedTeams = [];
    const usedTeamIds = new Set();
    const skipValue = Math.max(0, Number(skipSectionPositions) || 0);
    const addTeam = (team) => {
      if (team && team.id && !usedTeamIds.has(team.id)) {
        usedTeamIds.add(team.id);
        orderedTeams.push(team);
      }
    };

    sections.forEach((section) => {
      if (Array.isArray(section.baseLog) && section.baseLog.length > 0) {
        section.baseLog
          .map((entry) => eventTeams.find((team) => team.id === entry.teamId))
          .slice(skipValue)
          .forEach(addTeam);
        return;
      }

      if (Array.isArray(section.initialDraw) && section.initialDraw.length > 0) {
        section.initialDraw
          .map((entry) => eventTeams.find((team) => team.id === entry.id))
          .slice(skipValue)
          .forEach(addTeam);
      }
    });

    if (!orderedTeams.length) {
      return eventTeams;
    }

    eventTeams.forEach(addTeam);
    return orderedTeams;
  };

  const getCurrentLogTeams = (sourceEvent, logSections, skipSectionPositions) => {
    if (!sourceEvent || !Array.isArray(logSections)) {
      return [];
    }

    const eventTeams = sourceEvent.teams || [];
    const orderedTeams = [];
    const usedTeamIds = new Set();
    const skipValue = Math.max(0, Number(skipSectionPositions) || 0);

    logSections.forEach((sectionLog) => {
      const entries = Array.isArray(sectionLog.entries) ? sectionLog.entries : [];
      entries.slice(skipValue).forEach((entry) => {
        const team = eventTeams.find((item) => item.id === entry.teamId);
        if (team && team.id && !usedTeamIds.has(team.id)) {
          usedTeamIds.add(team.id);
          orderedTeams.push(team);
        }
      });
    });

    return orderedTeams;
  };

  const getOrderedImportTeams = useCallback(() => {
    if (!importSourceEvent) {
      return [];
    }

    const skipValue = Math.max(0, Number(watchImportSkipSectionPositions) || 0);

    if (watchImportLogType === 'current') {
      return getCurrentLogTeams(importSourceEvent, currentLogSections, skipValue);
    }

    return getBaseLogTeams(importSourceEvent, skipValue);
  }, [importSourceEvent, watchImportLogType, currentLogSections, watchImportSkipSectionPositions]);

  const orderedImportTeams = getOrderedImportTeams();
  const maxImportTeams = orderedImportTeams.length;

  const getSelectedImportTeams = useCallback(
    (teams) => {
      if (!teams.length) {
        return [];
      }

      const fromValue = Math.max(1, Number(watchImportPositionFrom) || 1);
      const toValue = Math.min(Number(watchImportPositionTo) || teams.length, teams.length);
      return teams.slice(fromValue - 1, toValue);
    },
    [watchImportPositionFrom, watchImportPositionTo]
  );

  const selectedImportTeams = useMemo(
    () => getSelectedImportTeams(orderedImportTeams),
    [orderedImportTeams, getSelectedImportTeams]
  );

  const firstSectionTeamCount = useMemo(() => {
    if (!importSourceEvent) {
      return 0;
    }

    const sections = importSourceEvent.sections || [];
    if (!sections.length) {
      return 0;
    }

    if (watchImportLogType === 'current') {
      return currentLogSections[0]?.entries?.length || 0;
    }

    const firstSection = sections[0];
    if (Array.isArray(firstSection.baseLog) && firstSection.baseLog.length > 0) {
      return firstSection.baseLog.length;
    }

    if (Array.isArray(firstSection.initialDraw) && firstSection.initialDraw.length > 0) {
      return firstSection.initialDraw.length;
    }

    return (importSourceEvent.teams || []).length;
  }, [importSourceEvent, watchImportLogType, currentLogSections]);

  const totalImportPlayers = useMemo(() => {
    return selectedImportTeams.reduce(
      (total, team) => total + (Array.isArray(team.members) ? team.members.length : 0),
      0
    );
  }, [selectedImportTeams]);

  useEffect(() => {
    if (!watchImportPlayers) {
      return;
    }

    if (maxImportTeams > 0) {
      const nextFrom = Math.max(1, Number(watchImportPositionFrom) || 1);
      const nextTo = Math.max(1, Number(watchImportPositionTo) || maxImportTeams);
      const clampedFrom = Math.min(nextFrom, maxImportTeams);
      const clampedTo = Math.min(nextTo, maxImportTeams);

      if (clampedFrom !== watchImportPositionFrom) {
        setValue('importPositionFrom', clampedFrom);
      }

      if (clampedTo !== watchImportPositionTo) {
        setValue('importPositionTo', clampedTo);
      }
    }
  }, [
    watchImportPlayers,
    watchImportPositionFrom,
    watchImportPositionTo,
    maxImportTeams,
    setValue
  ]);

  useEffect(() => {
    if (!watchImportPlayers) {
      return;
    }

    if (watchImportLogType === 'current' && currentLogLoading) {
      return;
    }

    const maxSkip = Math.max(0, Number(firstSectionTeamCount) || 0);
    const nextSkip = Math.max(0, Number(watchImportSkipSectionPositions) || 0);
    const clampedSkip = maxSkip > 0 ? Math.min(nextSkip, maxSkip) : 0;

    if (clampedSkip !== watchImportSkipSectionPositions) {
      setValue('importSkipSectionPositions', clampedSkip);
    }
  }, [
    watchImportPlayers,
    watchImportSkipSectionPositions,
    firstSectionTeamCount,
    watchImportLogType,
    currentLogLoading,
    setValue
  ]);

  useEffect(() => {
    if (!watchImportPlayers || !importSourceEvent || maxImportTeams <= 0) {
      return;
    }

    const fromValue = Number(watchImportPositionFrom) || 1;
    const toValue = Number(watchImportPositionTo) || 1;
    if (fromValue === 1 && toValue === 1) {
      setValue('importPositionTo', maxImportTeams);
    }
  }, [
    watchImportPlayers,
    importSourceEvent,
    maxImportTeams,
    watchImportPositionFrom,
    watchImportPositionTo,
    setValue
  ]);

  const importRangeError = useMemo(() => {
    if (!watchImportPlayers) {
      return null;
    }

    if (!importSourceEvent) {
      return 'Select an event to import from.';
    }

    if (watchImportLogType === 'current' && currentLogLoading) {
      return 'Loading current log...';
    }

    if (maxImportTeams <= 0) {
      return 'No teams available for import.';
    }

    const fromValue = Number(watchImportPositionFrom);
    const toValue = Number(watchImportPositionTo);

    if (!Number.isFinite(fromValue) || !Number.isFinite(toValue)) {
      return 'Enter valid positions.';
    }

    if (fromValue < 1 || toValue < 1) {
      return 'Positions cannot be negative.';
    }

    if (fromValue > toValue) {
      return 'From position cannot be greater than To position.';
    }

    if (toValue > maxImportTeams) {
      return `Max position is ${maxImportTeams}.`;
    }

    return null;
  }, [
    watchImportPlayers,
    importSourceEvent,
    watchImportLogType,
    currentLogLoading,
    maxImportTeams,
    watchImportPositionFrom,
    watchImportPositionTo
  ]);

  const importSkipError = useMemo(() => {
    if (!watchImportPlayers) {
      return null;
    }

    if (watchImportLogType === 'current' && currentLogLoading) {
      return null;
    }

    const skipValue = Number(watchImportSkipSectionPositions);
    if (!Number.isFinite(skipValue)) {
      return 'Enter a valid skip value.';
    }

    if (skipValue < 0) {
      return 'Skip cannot be negative.';
    }

    if (firstSectionTeamCount <= 0 && skipValue > 0) {
      return 'No section positions available to skip.';
    }

    if (skipValue > firstSectionTeamCount) {
      return `Max skip is ${firstSectionTeamCount}.`;
    }

    return null;
  }, [
    watchImportPlayers,
    watchImportSkipSectionPositions,
    firstSectionTeamCount,
    watchImportLogType,
    currentLogLoading
  ]);

  const importCompatibilityError = useMemo(() => {
    if (!watchImportPlayers) {
      return null;
    }

    if (!importSourceEvent) {
      return null;
    }

    const sourceType = importSourceEvent?.gameFormat?.gameType;
    const targetType = selectedGameFormat?.gameType;

    if (!sourceType || !targetType) {
      return 'Select a game format to validate import.';
    }

    if (targetType === GameType.SINGLES && sourceType !== targetType) {
      return 'Cannot import non-Singles into a Singles event.';
    }

    if (sourceType === targetType) {
      return null;
    }

    const allowSinglesUpgrade = sourceType === GameType.SINGLES;
    const allowPairsToFours = sourceType === GameType.PAIRS && targetType === GameType.FOURS;

    if (!allowSinglesUpgrade && !allowPairsToFours) {
      return 'This import combination is not supported.';
    }

    const targetTeamSize = getTeamSize(targetType);
    if (!targetTeamSize) {
      return null;
    }

    if (watchImportAutoGroupPlayers && totalImportPlayers % targetTeamSize !== 0) {
      return `Selected players cannot be evenly split into ${getGameType(targetType)} teams.`;
    }

    return null;
  }, [
    watchImportPlayers,
    importSourceEvent,
    selectedGameFormat,
    watchImportAutoGroupPlayers,
    totalImportPlayers
  ]);

  const isAronSherriffPlayer = (player) =>
    `${player?.name}`.toLowerCase().trim() === 'aron' &&
    `${player?.surname}`.toLowerCase().trim() === 'sherriff';

  const getPlayersFromTeams = useCallback(
    (teams) => {
      const playerMap = new Map();
      teams.forEach((team) => {
        (team.members || []).forEach((player) => {
          if (player && player.id && !playerMap.has(player.id)) {
            playerMap.set(player.id, player);
          }
        });
      });

      const players = Array.from(playerMap.values());
      if (!watchImportOrderByPbaRanking) {
        return players;
      }

      const getClientRank = (player) => {
        if (isAronSherriffPlayer(player)) return 0;
        const key = player?.bsaNumber ?? player?.bsa_number;
        if (pbaRankingsMap.size > 0) {
          return key != null ? (pbaRankingsMap.get(String(key).trim()) ?? null) : null;
        }
        return player?.pbaRanking ?? player?.ranking ?? player?.rank ?? null;
      };

      return players.sort((a, b) => {
        const aRank = getClientRank(a);
        const bRank = getClientRank(b);

        if (aRank == null && bRank == null) return 0;
        if (aRank == null) return 1;
        if (bRank == null) return -1;
        return aRank - bRank;
      });
    },
    [watchImportOrderByPbaRanking, pbaRankingsMap] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const importTeamsAndPlayers = async (targetEventId) => {
    const orderedTeams = getOrderedImportTeams();
    if (orderedTeams.length === 0) {
      return;
    }

    const selectedTeams = getSelectedImportTeams(orderedTeams);

    if (selectedTeams.length === 0) {
      return;
    }

    const playersToImport = getPlayersFromTeams(selectedTeams);
    const sourceType = importSourceEvent?.gameFormat?.gameType;
    const targetType = selectedGameFormat?.gameType;
    const sourceTeamSize = getTeamSize(sourceType);
    const targetTeamSize = getTeamSize(targetType);

    let teamsToImport = selectedTeams;
    if (sourceTeamSize && targetTeamSize && sourceTeamSize < targetTeamSize) {
      if (watchImportAutoGroupPlayers) {
        teamsToImport = [];
        for (let i = 0; i < playersToImport.length; i += targetTeamSize) {
          const members = playersToImport.slice(i, i + targetTeamSize);
          teamsToImport.push({
            members,
            name: generateTeamName(targetType, members)
          });
        }
      } else {
        teamsToImport = [];
      }
    }

    // For same-type imports with PBA ranking: rebuild teams from ranked order when Aron is present
    if (
      watchImportOrderByPbaRanking &&
      targetTeamSize &&
      sourceTeamSize === targetTeamSize &&
      playersToImport.some(isAronSherriffPlayer)
    ) {
      teamsToImport = [];
      for (let i = 0; i < playersToImport.length; i += targetTeamSize) {
        const members = playersToImport.slice(i, i + targetTeamSize);
        teamsToImport.push({
          members,
          name: generateTeamName(targetType, members)
        });
      }
    }

    // One restore point for the whole import. The per-write coalescer would
    // otherwise make the point depend on which batch happened to start first;
    // skipSnapshot keeps the individual writes from adding more. The import
    // reports one audit record at the end instead of one per player.
    const importSnapshot = await eventService.captureEventSnapshot(
      targetEventId,
      'players.import'
    );
    await Promise.all(
      playersToImport.map((player) =>
        eventService.addPlayer(
          targetEventId,
          { ...player },
          { skipSnapshot: true, skipAudit: true }
        )
      )
    );
    await Promise.all(
      teamsToImport.map((team) =>
        eventService.addTeam(
          targetEventId,
          { ...team },
          { skipSnapshot: true, skipAudit: true }
        )
      )
    );
    eventService.reportDirectWrite(targetEventId, 'players.import', {
      snapshotId: importSnapshot?.snapshotId,
      entityId: importSourceEvent?.id || null,
      entityName: importSourceEvent?.name || 'Player import',
      metadata: {
        playerCount: playersToImport.length,
        teamCount: teamsToImport.length
      }
    });
  };

  const sortedImportEvents = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setHours(0, 0, 0, 0);
    cutoffDate.setDate(cutoffDate.getDate() - 1);

    const nextEvents = events.filter((evt) => {
      if (event && evt.id === event.id) {
        return false;
      }

      if (watchImportShowPastEvents) {
        return true;
      }

      const endDate = evt.endDate?.toDate?.() || new Date(evt.endDate);
      endDate.setHours(23, 59, 59, 999);
      return endDate >= cutoffDate;
    });

    return nextEvents.sort((a, b) => {
      const aDate = a.startDate?.toDate?.() || new Date(a.startDate);
      const bDate = b.startDate?.toDate?.() || new Date(b.startDate);
      return bDate - aDate;
    });
  }, [events, event, watchImportShowPastEvents]);

  const sourceImportGameType = importSourceEvent?.gameFormat?.gameType;
  const targetImportGameType = selectedGameFormat?.gameType;

  const showAutoGroupCheckbox =
    watchImportPlayers &&
    ((sourceImportGameType === GameType.SINGLES &&
      targetImportGameType &&
      targetImportGameType !== GameType.SINGLES) ||
      (sourceImportGameType === GameType.PAIRS && targetImportGameType === GameType.FOURS));

  const preview = useMemo(() => {
    if (!watchImportPlayers || !importSourceEvent) {
      return null;
    }

    const orderedTeams = getOrderedImportTeams();
    if (!orderedTeams.length) {
      return null;
    }

    const selectedTeams = getSelectedImportTeams(orderedTeams);
    const playersToImport = getPlayersFromTeams(selectedTeams);

    const sourceType = importSourceEvent?.gameFormat?.gameType;
    const targetType = selectedGameFormat?.gameType;
    const sourceTeamSize = getTeamSize(sourceType);
    const targetTeamSize = getTeamSize(targetType);

    let teamsToImport = selectedTeams;
    if (sourceTeamSize && targetTeamSize && sourceTeamSize < targetTeamSize) {
      if (watchImportAutoGroupPlayers && targetTeamSize) {
        teamsToImport = [];
        for (let i = 0; i < playersToImport.length; i += targetTeamSize) {
          const members = playersToImport.slice(i, i + targetTeamSize);
          teamsToImport.push({
            members,
            name: generateTeamName(targetType, members)
          });
        }
      } else {
        teamsToImport = [];
      }
    }

    // For same-type imports with PBA ranking: rebuild teams from ranked order when Aron is present
    if (
      watchImportOrderByPbaRanking &&
      targetTeamSize &&
      sourceTeamSize === targetTeamSize &&
      playersToImport.some(isAronSherriffPlayer)
    ) {
      teamsToImport = [];
      for (let i = 0; i < playersToImport.length; i += targetTeamSize) {
        const members = playersToImport.slice(i, i + targetTeamSize);
        teamsToImport.push({
          members,
          name: generateTeamName(targetType, members)
        });
      }
    }

    return {
      sourceEventName: importSourceEvent?.name || 'Unknown event',
      logType: watchImportLogType,
      positionFrom: watchImportPositionFrom,
      positionTo: watchImportPositionTo,
      skipSectionPositions: Math.max(0, Number(watchImportSkipSectionPositions) || 0),
      selectedTeamsCount: selectedTeams.length,
      players: playersToImport,
      teams: teamsToImport,
      targetGameType: targetType
    };
  }, [
    watchImportPlayers,
    importSourceEvent,
    watchImportLogType,
    watchImportPositionFrom,
    watchImportPositionTo,
    selectedGameFormat,
    watchImportAutoGroupPlayers,
    watchImportOrderByPbaRanking,
    currentLogSections,
    watchImportSkipSectionPositions,
    getOrderedImportTeams,
    getSelectedImportTeams,
    getPlayersFromTeams
  ]);

  // Leave auto-group toggling to the user once the option is available.

  return {
    watchImportPlayers,
    sortedImportEvents,
    currentLogLoading,
    maxImportTeams,
    importRangeError,
    importSkipError,
    firstSectionTeamCount,
    showAutoGroupCheckbox,
    targetImportGameType,
    watchImportAutoGroupPlayers,
    importCompatibilityError,
    importValidationError: importRangeError || importSkipError || importCompatibilityError,
    watchImportShowPastEvents,
    watchImportOrderByPbaRanking,
    watchImportPbaRankingDate,
    pbaRankingsMap,
    pbaRankingsLoading,
    pbaRankingsError,
    importTeamsAndPlayers,
    preview
  };
};

export default usePlayersImport;
